import * as THREE from "three";

const sm = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
const lerp = (a, b, t) => a + (b - a) * t;
let seed = 7;
const rnd = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };

function prism(w, h, d, mat) {
  const s = new THREE.Shape();
  s.moveTo(-w / 2, 0); s.lineTo(w / 2, 0); s.lineTo(0, h); s.closePath();
  const g = new THREE.ExtrudeGeometry(s, { depth: d, bevelEnabled: false });
  g.translate(0, 0, -d / 2);
  const m = new THREE.Mesh(g, mat);
  m.castShadow = true;
  return m;
}

function box(w, h, d, mat, x = 0, y = 0, z = 0) {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  m.position.set(x, y, z);
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

const TRIO = [[-1.6, 0.9], [0, 0.9], [1.6, 0.9]];

function room(w, h, d, wall, floor, glow, front = TRIO) {
  const g = new THREE.Group();
  const t = 0.15;
  g.add(box(w, t, d, floor, 0, t / 2, 0));
  g.add(box(w, h, t, wall, 0, h / 2, -d / 2));
  g.add(box(w, h, t, wall, 0, h / 2, d / 2));
  g.add(box(t, h, d, wall, -w / 2, h / 2, 0));
  g.add(box(t, h, d, wall, w / 2, h / 2, 0));
  front.forEach(([x, ww]) => g.add(box(ww, 0.9, 0.05, glow, x, 1.4, d / 2 + 0.08)));
  TRIO.forEach(([x, ww]) => g.add(box(ww, 0.9, 0.05, glow, x, 1.4, -d / 2 - 0.08)));
  [-1.2, 1.2].forEach((z) => {
    g.add(box(0.05, 0.9, 0.9, glow, -w / 2 - 0.08, 1.4, z));
    g.add(box(0.05, 0.9, 0.9, glow, w / 2 + 0.08, 1.4, z));
  });
  return g;
}

export const FINISH_STEPS = [["Shaker Ivory", "#EFE6D6", 0], ["Handleless Sage", "#8A9A7E", 2450], ["Graphite Gloss", "#3B3F45", 3100], ["Warm Sand", "#D8B98A", 1800]];
export const personaliseIndex = (p) => Math.min(FINISH_STEPS.length - 1, Math.floor(sm(0.63, 0.79, p) * FINISH_STEPS.length));

export function createHouseScene(canvas) {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  const scene = new THREE.Scene();
  scene.fog = new THREE.Fog(0x071526, 35, 95);
  const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 300);

  scene.add(new THREE.HemisphereLight(0xfff1dc, 0x0f2a47, 0.85));
  const sun = new THREE.DirectionalLight(0xffd29a, 2.4);
  sun.position.set(14, 22, 10);
  sun.castShadow = true;
  sun.shadow.mapSize.set(1024, 1024);
  Object.assign(sun.shadow.camera, { left: -20, right: 20, top: 20, bottom: -20 });
  scene.add(sun);

  const ground = new THREE.Mesh(new THREE.CircleGeometry(80, 64), new THREE.MeshStandardMaterial({ color: 0x14243a, roughness: 1 }));
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  scene.add(ground);
  const road = new THREE.Mesh(new THREE.RingGeometry(13, 15.5, 96), new THREE.MeshStandardMaterial({ color: 0x2c2f2d, roughness: 1 }));
  road.rotation.x = -Math.PI / 2;
  road.position.y = 0.02;
  scene.add(road);

  const wall = new THREE.MeshStandardMaterial({ color: 0xefe6d6, roughness: 0.85 });
  const floorMat = new THREE.MeshStandardMaterial({ color: 0xb48a5a, roughness: 0.7 });
  const roofMat = new THREE.MeshStandardMaterial({ color: 0x353a3f, roughness: 0.8 });
  const glow = new THREE.MeshStandardMaterial({ color: 0xffd58a, emissive: 0xffaa44, emissiveIntensity: 1.3 });
  const gold = new THREE.MeshStandardMaterial({ color: 0xd4a12a, emissive: 0xd4a12a, emissiveIntensity: 0.6 });

  const house = new THREE.Group();
  const lower = room(6, 2.6, 5, wall, floorMat, glow, [[-0.8, 2.5]]);
  lower.add(box(1, 2, 0.06, new THREE.MeshStandardMaterial({ color: 0x0f2a47 }), 2.2, 1, 2.56), box(1.6, 0.12, 1.2, roofMat, 2.2, 2.3, 3));
  lower.add(box(1.8, 0.9, 0.9, gold, -1, 0.6, 0), box(2, 0.6, 0.8, new THREE.MeshStandardMaterial({ color: 0x5b6b5e }), 1.5, 0.45, -1.4));
  const upper = room(6, 2.4, 5, wall, floorMat, glow);
  upper.add(box(1.6, 0.5, 2.1, new THREE.MeshStandardMaterial({ color: 0xe8e1d4 }), -1.3, 0.4, -0.8));
  const roof = prism(6.8, 2.2, 5.6, roofMat);
  const garage = box(3, 2.4, 4.4, wall, 4.6, 1.2, 0.3);
  house.add(lower, upper, roof, garage);
  scene.add(house);

  const rings = [[-1, 1.2, 0], [1.5, 1.1, -1.4], [-1.3, 1, -0.8]].map(([x, y, z], i) => {
    const r = new THREE.Mesh(new THREE.TorusGeometry(0.35, 0.06, 12, 32), new THREE.MeshBasicMaterial({ color: 0xffd58a, transparent: true }));
    r.position.set(x, y, z);
    (i === 2 ? upper : lower).add(r);
    return r;
  });

  const pinCols = [0x10b981, 0x10b981, 0xf59e0b, 0xef4444, 0x10b981, 0x10b981, 0xf59e0b, 0x10b981, 0xef4444, 0x10b981, 0x10b981, 0xf59e0b];
  const pins = [];
  const hood = new THREE.MeshStandardMaterial({ color: 0xe4dccd, roughness: 0.9 });
  pinCols.forEach((c, i) => {
    const a = (i / pinCols.length) * Math.PI * 2;
    const g = new THREE.Group();
    g.position.set(Math.cos(a) * 21, 0, Math.sin(a) * 21);
    g.rotation.y = -a + Math.PI / 2;
    g.add(box(3.6, 2.4, 3.2, hood, 0, 1.2, 0));
    const r = prism(4, 1.5, 3.6, roofMat);
    r.position.y = 2.4;
    g.add(r);
    const pin = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({ color: c, emissive: c, emissiveIntensity: 0.8 });
    pin.add(new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 3, 8), mat));
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.55, 20, 20), mat);
    head.position.y = 1.7;
    pin.add(head);
    pin.position.y = 6;
    g.add(pin);
    pins.push(pin);
    scene.add(g);
  });

  const leaf = new THREE.MeshStandardMaterial({ color: 0x3d5a40, roughness: 1 });
  const trunk = new THREE.MeshStandardMaterial({ color: 0x5a4632 });
  for (let i = 0; i < 46; i++) {
    const a = i < 10 ? Math.PI + rnd() * Math.PI : rnd() * Math.PI * 2;
    const d = i < 10 ? 8 + rnd() * 3 : 26 + rnd() * 22;
    const t = new THREE.Group();
    t.position.set(Math.cos(a) * d, 0, Math.sin(a) * d);
    const s = 0.7 + rnd() * 0.8;
    t.scale.setScalar(s);
    t.add(box(0.25, 1, 0.25, trunk, 0, 0.5, 0));
    const c = new THREE.Mesh(new THREE.ConeGeometry(1, 2.6, 7), leaf);
    c.position.y = 2.2;
    c.castShadow = true;
    t.add(c);
    scene.add(t);
  }

  const palette = FINISH_STEPS.map(([, c]) => new THREE.Color(c));
  let target = 0, p = 0, raf = 0;
  const tiltAxis = new THREE.Vector3();
  const clock = new THREE.Clock();

  const resize = () => {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  };
  resize();
  window.addEventListener("resize", resize);

  const frame = () => {
    const t = clock.getElapsedTime();
    p += (target - p) * (reduced ? 1 : 0.06);
    const explode = sm(0.14, 0.28, p) * (1 - sm(0.36, 0.44, p));
    const map = sm(0.4, 0.46, p) * (1 - sm(0.52, 0.58, p));
    const close = sm(0.58, 0.64, p) * (1 - sm(0.82, 0.88, p));
    const pi = personaliseIndex(p);
    upper.position.y = 2.6 + explode * 2.6;
    roof.position.y = 5.0 + explode * 5.5;
    rings.forEach((r, i) => { r.material.opacity = explode * (0.6 + 0.4 * Math.sin(t * 3 + i)); r.rotation.x = Math.PI / 2; r.scale.setScalar(1 + 0.15 * Math.sin(t * 3 + i)); });
    pins.forEach((pin, i) => { const s = Math.max(0.001, map); pin.scale.setScalar(s); pin.position.y = 5 + Math.sin(t * 2 + i) * 0.3; });
    wall.color.lerp(palette[pi], 0.08);
    const mobile = camera.aspect < 0.9 ? 1.45 : 1;
    const ang = Math.PI / 2 - 0.75 + p * 1.3 + (reduced ? 0 : Math.sin(t * 0.25) * 0.08);
    const rad = lerp(lerp(15, 30, map), 17, close) * mobile;
    camera.position.set(Math.cos(ang) * rad, lerp(lerp(5.5, 34, map), 6, close) + explode * 4, Math.sin(ang) * rad);
    house.position.y = explode * 1.2;
    house.quaternion.setFromAxisAngle(tiltAxis.set(Math.sin(ang), 0, -Math.cos(ang)), explode * 0.38);
    camera.lookAt(camera.aspect > 1.2 ? -2.5 * (1 - map) : 0, 2.2 + explode * 2 - map * 2, 0);
    renderer.render(scene, camera);
    raf = requestAnimationFrame(frame);
  };
  frame();

  return {
    setProgress: (v) => { target = v; },
    dispose: () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      renderer.dispose();
      scene.traverse((o) => { o.geometry?.dispose?.(); });
    },
  };
}
